import React, { useCallback, useEffect, useRef, useState } from "react";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import * as style from "./FeatureGroup.module.scss";
import Card from "./Card";
import { featureGroupsReorder } from "../../services/RequestAccess/RequestAccess";
import { triggerToast } from "../../utils/toast";
import NoAvailablePermission from "../../Components/PermissionManage/NoAvailablePermission";

interface Feature {
  id: number;
  featureName: string;
  featureDescription: string | null;
  isChecked?: boolean;
}

interface ValueStream {
  groupId: number;
  id: number;
  name: string;
  description: string;
  groupDescription: string;
  features: Feature[];
  group: any;
}

interface FeatureGroupSectionProps {
  visibleGroups: ValueStream[][];
  isVisible: boolean;
  isEdit: boolean;
  fetchFeatureGroupsData: any;
}

interface ExpandedState {
  [key: number]: boolean;
}

const FeatureGroupSection: React.FC<FeatureGroupSectionProps> = ({
  visibleGroups = [],
  isVisible = false,
  isEdit = false,
  fetchFeatureGroupsData = () => null,
}) => {
  const scssClass = style as any;
  const [expandedRows, setExpandedRows] = useState<ExpandedState>({});
  const hasInitialized = useRef(false);

  const onDrop = async (draggedItem: any, targetItem: any) => {
    const newSequence = targetItem.sequence;

    if (newSequence === draggedItem.sequence) return;
    const payload = {
      moves: [
        {
          group_id: draggedItem.id,
          old_sequence: draggedItem.sequence,
          new_sequence: newSequence,
        },
      ],
    };

    try {
      const response = await featureGroupsReorder(payload);
      if (response?.error) {
        triggerToast(`${response?.error}`, "error");
      } else if (response?.status === "SUCCESS") {
        triggerToast(`${response?.message}`, "success");
        fetchFeatureGroupsData();
      }
    } catch (err) {}
  };

  const toggleRow = (rowIndex: number): void => {
    const shouldExpand = !expandedRows[rowIndex];

    setExpandedRows((prev) => ({
      ...prev,
      [rowIndex]: shouldExpand,
    }));
  };

  const toggleAllCards = useCallback(
    (expand: boolean): void => {
      const newExpandedRows: ExpandedState = {};
      visibleGroups.forEach((_, index) => {
        newExpandedRows[index] = expand;
      });
      setExpandedRows(newExpandedRows);
    },
    [visibleGroups],
  );

  useEffect(() => {
    if (!hasInitialized.current && visibleGroups?.length > 0) {
      toggleAllCards(true);
      hasInitialized.current = true;
    }
  }, [visibleGroups]);

  return (
    <>
      <div className={scssClass.featureGroupContainer}>
        <span>{isVisible ? "Visible" : "Hidden"} Feature Groups</span>
        <div className={scssClass.clickSection}>
          <span
            role="button"
            onKeyDown={() => {}}
            data-testid={"expand-section"}
            tabIndex={0}
            onClick={() => toggleAllCards(true)}
          >
            Expand Section
          </span>
          <span className={scssClass.separator}>|</span>
          <span
            role="button"
            tabIndex={0}
            onClick={() => toggleAllCards(false)}
            onKeyDown={() => {}}
          >
            Collapse Section
          </span>
        </div>
      </div>
      <div className={scssClass.containermain}>
        <div
          className={scssClass.cardGridContainer}
          style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
        >
          {visibleGroups?.map((data, rowIndex) => (
            <div key={rowIndex} className={scssClass.rowMap}>
              <div className={scssClass.carddiv}>
                {data?.map((item, index) => (
                  <div
                    key={item?.id}
                    className={scssClass.rowMap}
                    onDragStart={(e) => {
                      e.dataTransfer.setData(
                        "draggedItem",
                        JSON.stringify(item),
                      );
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const draggedItem = e?.dataTransfer?.getData(
                        "draggedItem",
                      )
                        ? JSON?.parse(e?.dataTransfer?.getData("draggedItem"))
                        : {};
                      onDrop(draggedItem, item);
                    }}
                  >
                    <Card
                      key={item?.id}
                      id={item?.id}
                      name={item?.name}
                      description={item?.description}
                      group={item} // Group contains features now
                      isExpanded={expandedRows[rowIndex]}
                      isEdit={isEdit}
                      isVisible={isVisible}
                    />
                  </div>
                ))}
              </div>
              <button
                onClick={() => toggleRow(rowIndex)}
                className={scssClass.buttonRowExpandCollapse}
                data-testid={"expand"}
              >
                {expandedRows[rowIndex] ? (
                  <KeyboardArrowUpIcon className={scssClass.chevronUpDown} />
                ) : (
                  <KeyboardArrowDownIcon className={scssClass.chevronUpDown} />
                )}
              </button>
            </div>
          ))}
          {visibleGroups?.length === 0 ? (
            <NoAvailablePermission
              type={`${isVisible ? "Visible" : "Hidden"} Feature Groups`}
            />
          ) : null}
        </div>
      </div>
    </>
  );
};

export default FeatureGroupSection;
